import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuestionsFeed, useSession, useCreateSession, useUpdateSession, useCompleteSession, useCreateShare } from '../lib/api';
import { SwipeCard } from '../components/SwipeCard';
import { markFlashcardReviewed, flagFlashcard, unflagFlashcard } from '../lib/flashcard-progress';
import { scrollAppToTop } from '../lib/scroll';
import { motion, AnimatePresence, springs, Pressable, FadeIn, CountUp, Confetti, Skeleton } from '../lib/motion';
import { haptic } from '../lib/haptics';

const FC_TUTORIAL_KEY = 'fc-tutorial-dismissed';

export function FlashCardsPage() {
  const { sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const navigate = useNavigate();

  const { data: feed } = useQuestionsFeed({ class_section_id: sectionId });
  const { data: sessionData } = useSession(sessionId);
  const updateSession = useUpdateSession();
  const completeSession = useCompleteSession();

  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [gotItSet, setGotItSet] = useState<Set<string>>(new Set());
  const [wrongSet, setWrongSet] = useState<Set<string>>(new Set());
  const [sessionComplete, setSessionComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const hydratedRef = useRef<string | null>(null);
  const [sessionQuestionIds, setSessionQuestionIds] = useState<string[] | null>(null);
  const [shareStatus, setShareStatus] = useState<'idle' | 'sharing' | 'copied'>('idle');
  const createShare = useCreateShare();

  // Show tutorial on first use
  useEffect(() => {
    if (!localStorage.getItem(FC_TUTORIAL_KEY)) setShowTutorial(true);
  }, []);

  const dismissTutorial = useCallback(() => {
    setShowTutorial(false);
    localStorage.setItem(FC_TUTORIAL_KEY, '1');
  }, []);

  // All flashcard questions for this section
  const flashcards = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'flashcard'),
    [feed?.questions],
  );

  // Hydrate session state from server
  useEffect(() => {
    if (!sessionData?.session) return;
    const s = sessionData.session;
    const key = `${s.id}:${s.currentIndex}:${s.correctCount}:${s.questionsAnswered}`;
    if (hydratedRef.current === key) return;
    hydratedRef.current = key;

    setSessionQuestionIds(s.questionIds);
    setIdx(s.currentIndex);

    // Rebuild gotIt/wrong sets from session answers
    if (s.answers) {
      const g = new Set<string>();
      const w = new Set<string>();
      for (const [qId, ans] of Object.entries(s.answers)) {
        if (ans.correct) g.add(qId);
        else w.add(qId);
      }
      setGotItSet(g);
      setWrongSet(w);
    }
  }, [sessionData]);

  // Order flashcards by session question IDs
  const orderedCards = useMemo(() => {
    if (!sessionQuestionIds) return flashcards;
    const byId = new Map(flashcards.map((q: any) => [q.id, q]));
    return sessionQuestionIds.map(id => byId.get(id)).filter(Boolean) as any[];
  }, [flashcards, sessionQuestionIds]);

  const currentCard = orderedCards[idx];
  const payload = currentCard?.payload as any;
  const totalCards = orderedCards.length;
  const gotItCount = gotItSet.size;
  const wrongCount = wrongSet.size;

  const handleShare = useCallback(async () => {
    if (!currentCard || shareStatus === 'sharing') return;
    setShareStatus('sharing');
    try {
      const res = await createShare.mutateAsync({ question_id: currentCard.id, share_type: 'social' });
      const url = `https://app.squishyschool.com/q/${res.share_id}`;
      if (navigator.share) {
        await navigator.share({ title: 'Squishy School Flash Card', url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 2000);
    } catch {
      setShareStatus('idle');
    }
  }, [currentCard, shareStatus, createShare]);

  // Reset flip state when navigating
  useEffect(() => {
    setFlipped(false);
  }, [idx]);

  // Scroll to top on card change
  useEffect(() => {
    scrollAppToTop('smooth');
  }, [idx]);

  const persistProgress = useCallback((newIdx: number, correct: number, answered: number) => {
    if (!sessionId) return;
    updateSession.mutate({
      id: sessionId,
      currentIndex: newIdx,
      correctCount: correct,
      questionsAnswered: answered,
    });
  }, [sessionId, updateSession]);

  const handleSwipeRight = useCallback(() => {
    if (!currentCard) return;
    haptic.success();
    const newGotIt = new Set(gotItSet);
    newGotIt.add(currentCard.id);
    const newWrong = new Set(wrongSet);
    newWrong.delete(currentCard.id);
    setGotItSet(newGotIt);
    setWrongSet(newWrong);

    // Persist to localStorage
    markFlashcardReviewed(currentCard.id);
    unflagFlashcard(currentCard.id);

    const nextIdx = idx + 1;
    if (nextIdx >= totalCards) {
      haptic.success();
      setSessionComplete(true);
      if (sessionId) {
        completeSession.mutate(sessionId);
      }
    } else {
      setFlipped(false);
      setIdx(nextIdx);
      persistProgress(nextIdx, newGotIt.size, newGotIt.size + newWrong.size);
    }
  }, [currentCard, gotItSet, wrongSet, idx, totalCards, sessionId, completeSession, persistProgress]);

  const handleSwipeLeft = useCallback(() => {
    if (!currentCard) return;
    haptic.warning();
    const newWrong = new Set(wrongSet);
    newWrong.add(currentCard.id);
    const newGotIt = new Set(gotItSet);
    newGotIt.delete(currentCard.id);
    setGotItSet(newGotIt);
    setWrongSet(newWrong);

    // Persist to localStorage
    markFlashcardReviewed(currentCard.id);
    flagFlashcard(currentCard.id);

    const nextIdx = idx + 1;
    if (nextIdx >= totalCards) {
      setSessionComplete(true);
      if (sessionId) {
        completeSession.mutate(sessionId);
      }
    } else {
      setFlipped(false);
      setIdx(nextIdx);
      persistProgress(nextIdx, newGotIt.size, newGotIt.size + newWrong.size);
    }
  }, [currentCard, gotItSet, wrongSet, idx, totalCards, sessionId, completeSession, persistProgress]);

  const goBack = () => {
    if (idx > 0) {
      const newIdx = idx - 1;
      setFlipped(false);
      setIdx(newIdx);
      persistProgress(newIdx, gotItSet.size, gotItSet.size + wrongSet.size);
    }
  };

  const goNext = () => {
    if (idx < totalCards - 1) {
      const newIdx = idx + 1;
      setFlipped(false);
      setIdx(newIdx);
      persistProgress(newIdx, gotItSet.size, gotItSet.size + wrongSet.size);
    }
  };

  const revealCard = () => {
    if (!flipped) {
      haptic.light();
      setFlipped(true);
    }
  };

  // Session complete screen
  if (sessionComplete) {
    const accuracy = totalCards > 0 ? Math.round((gotItCount / totalCards) * 100) : 0;
    const perfect = totalCards > 0 && wrongCount === 0;
    const headline = perfect ? 'Perfect deck!' : accuracy >= 80 ? 'Deck crushed!' : accuracy >= 50 ? 'Nice progress!' : 'Deck complete';
    const subline = perfect
      ? `All ${totalCards} cards known. Beautifully done.`
      : `You worked through ${totalCards} cards.`;
    return (
      <div className="relative min-h-screen bg-slate-50 flex items-center justify-center p-4 overflow-hidden">
        <Confetti count={perfect ? 60 : accuracy >= 50 ? 40 : 24} duration={perfect ? 2 : 1.6} />
        <FadeIn className="w-full max-w-sm">
          <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-7 text-center">
            <motion.div
              initial={{ scale: 0.6, opacity: 0, rotate: -12 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={springs.bouncy}
              className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-squishy-teal to-squishy-tealDark flex items-center justify-center shadow-pop"
            >
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
                animate={{ rotate: [0, -8, 8, 0] }}
                transition={{ duration: 0.6, delay: 0.25 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </motion.svg>
            </motion.div>
            <h1 className="font-display text-2xl font-semibold text-squishy-dark mt-5">{headline}</h1>
            <p className="text-sm text-slate-500 mt-1">{subline}</p>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="rounded-2xl bg-green-50 border border-green-200 py-3">
                <div className="text-2xl font-bold text-green-600">{gotItCount}</div>
                <div className="text-[11px] font-medium text-slate-500 mt-0.5">Got it</div>
              </div>
              <div className="rounded-2xl bg-amber-50 border border-amber-200 py-3">
                <div className="text-2xl font-bold text-amber-600">{wrongCount}</div>
                <div className="text-[11px] font-medium text-slate-500 mt-0.5">To review</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-4xl font-bold text-squishy-primary tabular-nums">
                <CountUp value={accuracy} />%
              </div>
              <p className="text-xs text-slate-400 mt-1">of cards marked as known</p>
            </div>

            <Pressable
              haptic="medium"
              onClick={() => navigate(`/class/${sectionId}`)}
              className="mt-7 w-full bg-squishy-primary text-white py-3.5 rounded-2xl font-semibold text-sm shadow-soft"
            >
              Back to Class
            </Pressable>
          </div>
        </FadeIn>
      </div>
    );
  }

  if (!currentCard) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-lg mx-auto pt-16 space-y-4">
          <Skeleton className="h-2 w-1/3 mx-auto" />
          <Skeleton className="h-[340px] rounded-3xl" />
          <div className="flex justify-between">
            <Skeleton className="h-10 w-24 rounded-full" />
            <Skeleton className="h-10 w-24 rounded-full" />
          </div>
          <p className="text-center text-slate-400 text-sm pt-2">Loading flashcards...</p>
        </div>
      </div>
    );
  }

  const progressPct = ((idx + 1) / totalCards) * 100;

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Tutorial overlay */}
      {showTutorial && <FlashCardTutorial onDismiss={dismissTutorial} />}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b px-4 py-2">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          <Pressable
            haptic="light"
            onClick={() => navigate(`/class/${sectionId}`)}
            aria-label="Back to class"
            className="p-2.5 -ml-2 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Pressable>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-squishy-dark leading-tight">Flash Cards</div>
            <div className="text-[11px] text-slate-400 tabular-nums">{idx + 1} of {totalCards}</div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 tabular-nums">
              {gotItCount}
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 tabular-nums">
              {wrongCount}
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z" /></svg>
            </span>
            <Pressable
              haptic="light"
              onClick={handleShare}
              disabled={shareStatus === 'sharing'}
              aria-label="Share this card"
              title="Share this card"
              className="p-2.5 -mr-1.5 rounded-full text-slate-400 hover:text-squishy-primary hover:bg-teal-50 transition-colors"
            >
              {shareStatus === 'copied' ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px] text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              )}
            </Pressable>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-lg mx-auto mt-1.5">
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-squishy-teal to-squishy-tealDark rounded-full"
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={springs.soft}
            />
          </div>
        </div>
      </div>

      {/* Card area */}
      <div className="max-w-lg mx-auto px-4 pt-6">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentCard.id}
            initial={{ opacity: 0, y: 26, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.12 } }}
            transition={springs.soft}
          >
            <SwipeCard
              enabled={flipped}
              onSwipeRight={handleSwipeRight}
              onSwipeLeft={handleSwipeLeft}
            >
              {/* 3D flip stage */}
              <div style={{ perspective: 1400 }}>
                <motion.div
                  className="relative grid"
                  style={{ transformStyle: 'preserve-3d' }}
                  initial={false}
                  animate={{ rotateY: flipped ? 180 : 0, scale: flipped ? [1, 1.03, 1] : 1 }}
                  transition={{ rotateY: { type: 'spring', stiffness: 240, damping: 26 }, scale: { duration: 0.45, ease: 'easeOut' } }}
                  whileTap={flipped ? undefined : { scale: 0.985 }}
                  onClick={revealCard}
                  role={flipped ? undefined : 'button'}
                  tabIndex={flipped ? undefined : 0}
                  aria-label={flipped ? undefined : 'Reveal answer'}
                  onKeyDown={(e) => {
                    if (!flipped && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      revealCard();
                    }
                  }}
                >
                  {/* Front */}
                  <div
                    style={{ gridArea: '1 / 1', backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                    className={`min-h-[340px] rounded-3xl bg-gradient-to-br from-squishy-navy to-squishy-navySoft shadow-card ring-1 ring-white/10 flex flex-col items-center justify-center p-7 ${flipped ? 'pointer-events-none' : 'cursor-pointer'}`}
                  >
                    {payload?.topic && (
                      <span className="text-[11px] font-semibold text-squishy-teal/90 uppercase tracking-[0.14em] mb-5">
                        {payload.topic}
                      </span>
                    )}
                    <p className="text-xl font-semibold text-white text-center leading-relaxed">
                      {payload?.front}
                    </p>
                    <div className="mt-8 flex items-center gap-1.5 text-white/40 text-xs font-medium">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Tap to reveal
                    </div>
                  </div>

                  {/* Back */}
                  <div
                    style={{ gridArea: '1 / 1', backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                    className="min-h-[340px] rounded-3xl bg-white border border-slate-100 shadow-card p-6"
                    aria-hidden={!flipped}
                  >
                    {payload?.topic && (
                      <span className="inline-block text-[10px] font-semibold text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full uppercase tracking-wider mb-3">
                        {payload.topic}
                      </span>
                    )}
                    <p className="text-xs font-medium text-slate-400 leading-relaxed mb-3">{payload?.front}</p>
                    <div className="border-t border-slate-100 pt-4">
                      <p className="text-[15px] text-squishy-dark leading-relaxed whitespace-pre-line">
                        {payload?.back}
                      </p>
                    </div>
                    {payload?.category && (
                      <div className="mt-4 text-[10px] text-slate-400">
                        {payload.category}
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            </SwipeCard>
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between mt-6 px-1">
          <Pressable
            haptic="light"
            onClick={goBack}
            disabled={idx === 0}
            className="flex items-center gap-1 text-xs text-slate-500 font-medium px-4 py-3 rounded-full border border-slate-200 bg-white shadow-card disabled:opacity-30 disabled:shadow-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </Pressable>

          <AnimatePresence>
            {!flipped && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={springs.snappy}
              >
                <Pressable
                  haptic="light"
                  onClick={revealCard}
                  className="text-xs text-squishy-primary font-semibold px-5 py-3 rounded-full border border-teal-200 bg-teal-50"
                >
                  Show Answer
                </Pressable>
              </motion.div>
            )}
          </AnimatePresence>

          <Pressable
            haptic="light"
            onClick={goNext}
            disabled={idx >= totalCards - 1}
            className="flex items-center gap-1 text-xs text-slate-500 font-medium px-4 py-3 rounded-full border border-slate-200 bg-white shadow-card disabled:opacity-30 disabled:shadow-none"
          >
            Skip
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </Pressable>
        </div>
      </div>
    </div>
  );
}

// ── Flashcard Tutorial ────────────────────────────────────────────────────────

function FlashCardTutorial({ onDismiss }: { onDismiss: () => void }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      title: 'Tap to reveal',
      desc: 'Tap the card to flip it and check the answer against your recall.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-squishy-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      bg: 'bg-teal-50 border-teal-200',
    },
    {
      title: 'Swipe right: got it',
      desc: 'If you knew the answer, swipe right or tap "Got it" to mark it as known.',
      icon: (
        <svg className="w-12 h-12 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      ),
      bg: 'bg-green-50 border-green-200',
    },
    {
      title: 'Swipe left: review',
      desc: 'Need another pass? Swipe left or tap "Review" to flag it for later.',
      icon: (
        <svg className="w-12 h-12 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      ),
      bg: 'bg-amber-50 border-amber-200',
    },
  ];

  const current = steps[step]!;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onDismiss}>
      <motion.div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />
      <motion.div
        className="relative bg-white rounded-3xl max-w-sm w-full shadow-2xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.92, y: 14 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={springs.bouncy}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`px-6 pt-8 pb-4 ${current.bg} border-b flex flex-col items-center`}>
          <motion.div key={step} initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={springs.bouncy}>
            {current.icon}
          </motion.div>
          <h3 className="font-display text-lg font-semibold text-squishy-dark mt-3">{current.title}</h3>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-slate-600 text-center leading-relaxed">{current.desc}</p>
          <div className="flex justify-center gap-2 mt-5">
            {steps.map((_, i) => (
              <div key={i} className={`h-2 rounded-full transition-all duration-300 ${i === step ? 'bg-squishy-primary w-5' : 'bg-slate-200 w-2'}`} />
            ))}
          </div>
          <div className="mt-5 flex gap-3">
            {step < steps.length - 1 ? (
              <>
                <Pressable haptic="light" onClick={onDismiss} className="flex-1 text-sm text-slate-400 py-3 rounded-xl border">Skip</Pressable>
                <Pressable
                  haptic="light"
                  onClick={() => setStep(step + 1)}
                  className="flex-1 bg-squishy-primary text-white py-3 rounded-xl text-sm font-semibold shadow-soft"
                >
                  Next
                </Pressable>
              </>
            ) : (
              <Pressable
                haptic="medium"
                onClick={onDismiss}
                className="w-full bg-squishy-primary text-white py-3 rounded-xl text-sm font-semibold shadow-soft"
              >
                Start studying
              </Pressable>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
